import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError, rupiah, LOGO_URL } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePaged, Pager } from "@/components/Pager";
import { CreditCard, Plus, Loader2, Coins, History, Printer, Layers, Search, X } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");

// Kartu cetak 85mm x 55mm (landscape) sesuai desain
function AscCard({ card }) {
  return (
    <div id={`asc-card-${card.id}`} className="asc-card bg-white border border-stone-300 rounded-xl mx-auto"
      style={{ width: "85mm", height: "55mm", padding: "5mm", boxSizing: "border-box", position: "relative" }}>
      <div className="flex flex-col items-center">
        <div id={`asc-qr-${card.id}`} className="flex justify-center">
          <QRCodeCanvas value={card.card_number} size={120} level="M" includeMargin={false} />
        </div>
        <p className="font-mono font-bold tracking-wider text-stone-900 mt-1" style={{ fontSize: "11px" }}>{card.card_number}</p>
      </div>
      <div className="flex items-center gap-2 mt-2 mb-1">
        <div className="flex-1 h-px bg-emerald-800" />
        <span className="text-emerald-800" style={{ fontSize: "12px" }}>&#10070;</span>
        <div className="flex-1 h-px bg-emerald-800" />
      </div>
      <p className="text-center text-emerald-900 font-serif font-bold leading-tight" style={{ fontSize: "7px" }}>
        Kartu ini dapat digunakan untuk berbelanja di Kopontren Al-Khoir.<br />
        Kartu ini milik Pondok Pesantren Al-Khoir. Jika ditemukan, harap dikembalikan
        ke kantor Pondok Pesantren Al-Khoir atau menghubungi 08116251414.
      </p>
    </div>
  );
}

export default function AdminAscCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newCard, setNewCard] = useState(null);
  const [bulkCards, setBulkCards] = useState([]);
  const [bulkCount, setBulkCount] = useState("10");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [topup, setTopup] = useState(null);
  const [topupAmt, setTopupAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [selIds, setSelIds] = useState([]);
  const [printPool, setPrintPool] = useState([]);
  const [search, setSearch] = useState("");

  const load = () => { setLoading(true); api.get("/admin/asc/cards").then((r) => setCards(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const createCard = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/asc/cards");
      setNewCard(data);
      toast.success(`Kartu ASC ${data.card_number} dibuat`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const createBulk = async () => {
    const n = parseInt(bulkCount, 10);
    if (!n || n < 1 || n > 100) return toast.error("Jumlah kartu harus 1–100");
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/asc/cards/bulk", { count: n });
      setBulkCards(data);
      setNewCard(null);
      toast.success(`${data.length} kartu ASC dibuat`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const submitTopup = async () => {
    const amt = parseFloat(topupAmt);
    if (!amt || amt <= 0) return toast.error("Masukkan nominal top-up yang valid");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/asc/cards/${topup.card_number}/topup`, { amount: amt });
      toast.success(`Top-up berhasil. Saldo ${rupiah(data.balance)}`);
      setTopup(null); setTopupAmt(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openHistory = async (card) => {
    try {
      const { data } = await api.get(`/admin/asc/cards/${card.card_number}/history`);
      setHist(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const printCards = (list) => {
    const cardsHtml = list.map((card) => {
      const canvas = document.querySelector(`#asc-qr-${card.id} canvas`);
      const dataUrl = canvas ? canvas.toDataURL("image/png") : "";
      return `<div class="card">
        <div class="qr"><img src="${dataUrl}"/></div>
        <div class="num">${card.card_number}</div>
        <div class="div"><span class="line"></span><span class="orn">&#10070;</span><span class="line"></span></div>
        <div class="txt">Kartu ini dapat digunakan untuk berbelanja di Kopontren Al-Khoir.<br/>
        Kartu ini milik Pondok Pesantren Al-Khoir. Jika ditemukan, harap dikembalikan ke kantor Pondok Pesantren Al-Khoir atau menghubungi 08116251414.</div>
      </div>`;
    }).join("");
    const w = window.open("", "_blank", "width=800,height=600");
    if (!w) return toast.error("Popup diblokir. Izinkan popup untuk mencetak.");
    w.document.write(`<html><head><title>Kartu ASC</title>
      <style>
        @page { size: auto; margin: 10mm; }
        html,body{margin:0;padding:0;font-family:Georgia,'Times New Roman',serif;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
        .sheet{display:flex;flex-wrap:wrap;gap:6mm;}
        .card{width:85mm;height:55mm;min-width:85mm;min-height:55mm;max-width:85mm;max-height:55mm;padding:5mm;box-sizing:border-box;border:1px dashed #bbb;page-break-inside:avoid;overflow:hidden;}
        .qr{display:flex;justify-content:center;}
        .qr img{width:26mm;height:26mm;}
        .num{text-align:center;font-family:monospace;font-weight:bold;letter-spacing:1px;font-size:11px;margin:2px 0 0;color:#1c1917;}
        .div{display:flex;align-items:center;gap:6px;margin:6px 0 3px;}
        .div .line{flex:1;height:1px;background:#065f46;}
        .div .orn{color:#065f46;font-size:13px;}
        .txt{text-align:center;color:#064e3b;font-weight:bold;font-size:7px;line-height:1.25;}
      </style></head><body onload="window.focus();window.print();">
      <div class="sheet">${cardsHtml}</div></body></html>`);
    w.document.close();
  };

  const printCard = (card) => printCards([card]);

  const openPrintDialog = () => { setSelIds([]); setPrintOpen(true); };
  const toggleSel = (id) => setSelIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = cards.length > 0 && selIds.length === cards.length;
  const toggleAll = () => setSelIds(allSelected ? [] : cards.map((c) => c.id));
  const doPrintSelected = () => {
    const list = cards.filter((c) => selIds.includes(c.id));
    if (list.length === 0) return toast.error("Pilih minimal satu kartu");
    setPrintPool(list);
    setPrintOpen(false);
    setTimeout(() => printCards(list), 400);
  };

  const filtered = search.trim()
    ? cards.filter((c) => c.card_number.includes(search.trim()))
    : cards;
  const pg = usePaged(filtered, 10);

  return (
    <div>
      <PageHeader
        title="Kartu ASC (Al-Khoir Smart Card)"
        subtitle="Kartu belanja anonim (tanpa identitas). Buat kartu, top-up saldo tunai, cetak QR, dan lihat riwayat."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-white border border-stone-200 rounded-xl pl-3 pr-1.5 py-1">
              <span className="text-xs text-stone-500">Jumlah</span>
              <Input type="number" min="1" max="100" value={bulkCount} onChange={(e) => setBulkCount(e.target.value)} data-testid="asc-bulk-count" className="w-16 h-8 rounded-lg border-0 shadow-none px-1 text-center focus-visible:ring-0" />
              <Button size="sm" onClick={createBulk} disabled={bulkBusy} data-testid="asc-bulk-button" variant="outline" className="rounded-lg h-8">
                {bulkBusy ? <Loader2 size={15} className="animate-spin" /> : <><Layers size={15} className="mr-1" /> Buat Banyak</>}
              </Button>
            </div>
            <Button onClick={openPrintDialog} data-testid="asc-printmany-button" variant="outline" className="rounded-xl">
              <Printer size={18} className="mr-2" /> Cetak Banyak
            </Button>
            <Button onClick={createCard} disabled={creating} data-testid="asc-create-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
              {creating ? <Loader2 size={18} className="animate-spin mr-2" /> : <Plus size={18} className="mr-2" />} Buat Kartu Baru
            </Button>
          </div>
        }
      />

      {bulkCards.length > 0 && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="asc-bulk-cards">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm font-medium text-emerald-800">{bulkCards.length} kartu baru dibuat — cetak dalam satu lembar:</p>
            <div className="flex gap-2">
              <Button onClick={() => printCards(bulkCards)} data-testid="asc-print-bulk" className="rounded-xl bg-stone-900 hover:bg-stone-800"><Printer size={16} className="mr-1.5" /> Cetak Semua ({bulkCards.length})</Button>
              <Button variant="outline" onClick={() => setBulkCards([])} className="rounded-xl">Tutup</Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 justify-items-center">
            {bulkCards.map((c) => (<div key={c.id} style={{ transform: "scale(0.85)" }}><AscCard card={c} /></div>))}
          </div>
        </div>
      )}

      {newCard && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="asc-new-card">
          <p className="text-sm font-medium text-emerald-800 mb-3">Kartu baru dibuat — siap dicetak:</p>
          <AscCard card={newCard} />
          <div className="flex justify-center gap-2 mt-4">
            <Button onClick={() => printCard(newCard)} data-testid="asc-print-new" className="rounded-xl bg-stone-900 hover:bg-stone-800"><Printer size={16} className="mr-1.5" /> Cetak Kartu</Button>
            <Button variant="outline" onClick={() => setNewCard(null)} className="rounded-xl">Tutup</Button>
          </div>
        </div>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="asc-search" placeholder="Cari nomor kartu…" className="rounded-xl bg-white h-11 pl-9 pr-9" />
        {search && (
          <button onClick={() => setSearch("")} data-testid="asc-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"><X size={16} /></button>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : cards.length === 0 ? (
          <Empty><CreditCard className="mx-auto mb-2" /> Belum ada kartu ASC. Klik "Buat Kartu Baru".</Empty>
        ) : filtered.length === 0 ? (
          <Empty><Search className="mx-auto mb-2" /> Tidak ada kartu dengan nomor "{search}".</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nomor Kartu</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Dibuat</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((c) => (
                <TableRow key={c.id} data-testid={`asc-row-${c.card_number}`}>
                  <TableCell className="font-mono font-semibold text-stone-800">{c.card_number}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-emerald-700">{rupiah(c.balance)}</TableCell>
                  <TableCell className="text-sm text-stone-500">{fmt(c.created_at)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => { setTopup(c); setTopupAmt(""); }} data-testid={`asc-topup-${c.card_number}`} className="rounded-lg text-emerald-700 border-emerald-200"><Coins size={14} className="mr-1" /> Top-Up</Button>
                    <Button size="sm" variant="outline" onClick={() => setNewCard(c)} className="ml-1 rounded-lg"><CreditCard size={14} className="mr-1" /> QR</Button>
                    <Button size="sm" variant="outline" onClick={() => openHistory(c)} data-testid={`asc-history-${c.card_number}`} className="ml-1 rounded-lg"><History size={14} className="mr-1" /> Riwayat</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {!loading && filtered.length > 0 && <Pager {...pg} />}

      <Dialog open={!!topup} onOpenChange={(o) => !o && setTopup(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Top-Up Saldo ASC</DialogTitle></DialogHeader>
          {topup && (
            <div className="space-y-4">
              <div className="rounded-xl bg-stone-50 border p-3 text-sm">
                <p className="font-mono font-semibold text-stone-800">{topup.card_number}</p>
                <p className="text-stone-500">Saldo saat ini: <b className="text-emerald-700">{rupiah(topup.balance)}</b></p>
              </div>
              <div className="space-y-2">
                <Label>Nominal Top-Up (Rp, tunai)</Label>
                <Input type="number" inputMode="numeric" data-testid="asc-topup-amount" value={topupAmt} onChange={(e) => setTopupAmt(e.target.value)} className="rounded-xl" placeholder="cth: 50000" />
              </div>
              <DialogFooter>
                <Button onClick={submitTopup} disabled={busy} data-testid="asc-topup-submit" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : "Tambah Saldo"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Riwayat Kartu {hist?.card?.card_number}</DialogTitle></DialogHeader>
          {hist && (
            <div>
              <p className="text-sm text-stone-500 mb-3">Saldo saat ini: <b className="text-emerald-700">{rupiah(hist.card.balance)}</b></p>
              <div className="max-h-80 overflow-y-auto">
                {hist.rows.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-6">Belum ada transaksi.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow className="bg-stone-50"><TableHead>Waktu</TableHead><TableHead>Keterangan</TableHead><TableHead className="text-right">Jumlah</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {hist.rows.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell className="text-xs whitespace-nowrap">{fmt(t.created_at)}</TableCell>
                          <TableCell className="text-sm">{t.type === "topup" ? "Top-up tunai" : t.note}</TableCell>
                          <TableCell className={`text-right font-mono text-sm font-semibold ${t.amount >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{t.amount >= 0 ? "+" : ""}{rupiah(t.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Pilih Kartu untuk Dicetak</DialogTitle></DialogHeader>
          {cards.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-6">Belum ada kartu.</p>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <button type="button" onClick={toggleAll} data-testid="asc-print-selectall" className="text-sm font-medium text-emerald-700 hover:underline">
                  {allSelected ? "Batal pilih semua" : "Pilih semua"}
                </button>
                <span className="text-xs text-stone-500">{selIds.length} dipilih</span>
              </div>
              <div className="max-h-80 overflow-y-auto border border-stone-100 rounded-xl divide-y">
                {cards.map((c) => (
                  <label key={c.id} data-testid={`asc-print-opt-${c.card_number}`} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-stone-50">
                    <input type="checkbox" checked={selIds.includes(c.id)} onChange={() => toggleSel(c.id)} className="w-4 h-4 accent-emerald-600" />
                    <span className="font-mono text-sm text-stone-800 flex-1">{c.card_number}</span>
                    <span className="text-xs font-mono text-emerald-700">{rupiah(c.balance)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintOpen(false)} className="rounded-xl">Batal</Button>
            <Button onClick={doPrintSelected} disabled={selIds.length === 0} data-testid="asc-print-selected" className="bg-stone-900 hover:bg-stone-800 rounded-xl">
              <Printer size={16} className="mr-1.5" /> Cetak Terpilih ({selIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div style={{ position: "absolute", left: "-99999px", top: 0 }} aria-hidden="true">
        {printPool.map((c) => (
          <div key={c.id} id={`asc-qr-${c.id}`}><QRCodeCanvas value={c.card_number} size={120} level="M" includeMargin={false} /></div>
        ))}
      </div>
    </div>
  );
}
